import { Box, Stack, Tooltip, Typography } from "@mui/material";
import RespondentNotes from "features/Notes/RespondentNotes";
import { ReactElement, ReactNode } from "react";
import ParticipantDto from "types/ParticipantDto";
import { getPluralized } from "utils/display";
import { DataGetterProps } from "..";
import VideoData_Item from "../components/VideoData_Item";
import DATA_ITEM from "../types/DATA_ITEM";
import getAnswer from "./getAnswer";

interface DataGetters {
  [infoItem: string]: (
    ...params: unknown[]
  ) => ReactNode | ReactElement | undefined;
}

function renderParticipantNames(participants: ParticipantDto[]) {
  const sortedParticipantNames = participants
    .sort((a, b) => a.firstName.localeCompare(b.firstName))
    .map((p) => `${p.firstName} ${p.lastName.charAt(0)}.`);
  return (
    <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
      <Box>{sortedParticipantNames[0]}</Box>
      <Box>
        {participants.length > 1 && (
          <Tooltip
            title={
              <Stack>
                {sortedParticipantNames.slice(1).map((name, index) => (
                  <Box key={index}>{name}</Box>
                ))}
              </Stack>
            }
          >
            <Typography sx={{ fontWeight: "bold" }}>
              and {getPluralized("other", participants.length - 1, true)}
            </Typography>
          </Tooltip>
        )}
      </Box>
    </Stack>
  );
}

const dataGetters: DataGetters = {
  [DATA_ITEM.PARTICIPANT_NAME_WITH_NOTES]: ({
    participants,
  }: DataGetterProps) => {
    if (!participants || participants.length === 0) return undefined;

    return (
      <VideoData_Item>
        {renderParticipantNames(participants)}
        {participants.length === 1 && (
          <RespondentNotes participantId={participants[0].id} />
        )}
      </VideoData_Item>
    );
  },
  [DATA_ITEM.PARTICIPANT_NAME]: ({ participants }: DataGetterProps) => {
    if (!participants || participants.length === 0) return undefined;

    return (
      <VideoData_Item>{renderParticipantNames(participants)}</VideoData_Item>
    );
  },
  [DATA_ITEM.SCRIPT_TITLE]: ({ script, showLabel }: DataGetterProps) => (
    <VideoData_Item label={showLabel ? "Script" : undefined}>
      {script?.title}
    </VideoData_Item>
  ),
  [DATA_ITEM.VIDEO_TITLE]: ({
    video,
    question,
    showLabel,
  }: DataGetterProps) => (
    <VideoData_Item label={showLabel ? "Question" : undefined}>
      {question?.orderNumber ? `Q:${question.orderNumber} ` : ""}
      {question?.title || video?.title}
    </VideoData_Item>
  ),
  [DATA_ITEM.ANSWER]: ({ answer, question, showLabel }: DataGetterProps) => (
    <VideoData_Item label={showLabel ? "Answer" : undefined}>
      {getAnswer(answer, question?.options)}
    </VideoData_Item>
  ),
  [DATA_ITEM.VIDEO_DESCRIPTION]: ({ video, showLabel }: DataGetterProps) => (
    <VideoData_Item label={showLabel ? "Description" : undefined}>
      {video?.description}
    </VideoData_Item>
  ),
  [DATA_ITEM.VIDEO_FIRST_NOTE_OR_TITLE]: ({ note, video }: DataGetterProps) =>
    note ? <VideoData_Item>{note?.content}</VideoData_Item> : video?.title,
};

export default dataGetters;
